import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { auth } from "@/shared/api/graphqlClient";
import { LanguageProvider, useLanguage } from "@/shared/components/shared/LanguageContext";
import LanguageSwitcher from "@/shared/components/shared/LanguageSwitcher";
import {
  LayoutDashboard,
  Building2,
  Receipt,
  ArrowDownToLine,
  ArrowUpFromLine,
  Route,
  Webhook,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Sparkles,
  Bell,
  User,
  ChevronLeft,
  ChevronRight,
  Server,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import NotificationDropdown from "@/shared/components/shared/NotificationDropdown";

const getNavItems = (t) => [
  { name: t("dashboard"), icon: LayoutDashboard, page: "ResellerDashboard" },
  { name: t("transactions"), icon: Receipt, page: "ResellerTransactions" },
  { name: t("deposits"), icon: ArrowDownToLine, page: "ResellerDeposits" },
  { name: t("withdrawals"), icon: ArrowUpFromLine, page: "ResellerWithdrawals" },
  { name: t("merchants"), icon: Building2, page: "ResellerMerchants" },
  { name: t("providers"), icon: Server, page: "ResellerProviders", viewOnly: true },
  { name: t("routing"), icon: Route, page: "ResellerRouting" },
  { name: t("webhooks"), icon: Webhook, page: "ResellerWebhooks" },
  { name: t("settlements"), icon: FileText, page: "ResellerSettlements" },
  { name: t("settings"), icon: Settings, page: "ResellerSettings" },
];

function ResellerLayoutContent({ children, currentPage, resellerName = "Reseller Partner", resellerCode = "" }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const navItems = getNavItems(t);

  const handleLogout = () => {
    auth.logout();
    window.location.href = '/ResellerLogin';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        .reseller-sidebar {
          background: linear-gradient(180deg, #7c3aed 0%, #6d28d9 100%);
        }
        .reseller-sidebar::-webkit-scrollbar,
        .reseller-sidebar nav::-webkit-scrollbar {
          width: 6px;
        }
        .reseller-sidebar::-webkit-scrollbar-track,
        .reseller-sidebar nav::-webkit-scrollbar-track {
          background: transparent;
        }
        .reseller-sidebar::-webkit-scrollbar-thumb,
        .reseller-sidebar nav::-webkit-scrollbar-thumb {
          background: rgba(167, 139, 250, 0.4);
          border-radius: 3px;
        }
        .reseller-sidebar::-webkit-scrollbar-thumb:hover,
        .reseller-sidebar nav::-webkit-scrollbar-thumb:hover {
          background: rgba(167, 139, 250, 0.6);
        }
        .reseller-nav-item {
          transition: all 0.2s ease;
        }
        .reseller-nav-item:hover {
          transform: translateX(4px);
        }
        .reseller-nav-item.active {
          background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
          box-shadow: 0 4px 12px rgba(167, 139, 250, 0.4);
        }
      `}</style>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-violet-900 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-white">Demo Gateway</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="relative text-white">
            <Bell className="h-5 w-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-violet-400 rounded-full" />
          </Button>
        </div>
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`reseller-sidebar fixed top-0 left-0 h-screen z-50 transition-all duration-300 ease-in-out
          ${collapsed ? "w-20" : "w-64"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* Logo */}
        <div className={`h-16 flex items-center border-b border-violet-600 ${collapsed ? "justify-center px-2" : "px-6"}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-violet-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            {!collapsed && (
              <div>
                <h1 className="font-bold text-white text-lg tracking-tight">Demo Gateway</h1>
                <p className="text-[10px] text-violet-300 uppercase tracking-wider">{t("resellerPanel")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-14rem)]">
          {!collapsed && (
            <p className="text-[10px] uppercase tracking-wider text-violet-300 font-medium mb-3 px-3">
              {t("navigation")}
            </p>
          )}

          {navItems.map((item) => {
            const isActive = currentPage === item.page;
            return (
              <Link
                key={item.page}
                to={createPageUrl(item.page)}
                onClick={() => setMobileOpen(false)}
                className={`reseller-nav-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                  ${isActive
                    ? "active text-white"
                    : "text-violet-200 hover:text-white hover:bg-violet-700/50"
                  }
                  ${collapsed ? "justify-center" : ""}
                `}
              >
                <item.icon className={`h-5 w-5 flex-shrink-0 ${isActive ? "text-white" : ""}`} />
                {!collapsed && (
                  <div className="flex items-center justify-between flex-1">
                    <span>{item.name}</span>
                    {item.viewOnly && (
                      <span className="text-[10px] bg-violet-500/50 px-1.5 py-0.5 rounded">{t("viewOnlyBadge")}</span>
                    )}
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Reseller Info */}
        {!collapsed && (
          <div className="absolute bottom-16 left-4 right-4">
            <div className="bg-violet-700/50 rounded-xl p-3 border border-violet-600">
              <p className="text-[10px] text-violet-300 mb-0.5">{t("loggedInAs")}</p>
              <p className="text-white font-semibold text-sm truncate">{resellerName}</p>
              {resellerCode && <p className="text-xs text-violet-300 mt-0.5">{resellerCode}</p>}
            </div>
          </div>
        )}

        {/* Collapse Button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute bottom-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-violet-600 items-center justify-center text-violet-300 hover:text-white hover:bg-violet-500 transition-colors"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${collapsed ? "lg:pl-20" : "lg:pl-64"}`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 hidden lg:flex h-16 items-center justify-between px-6 bg-white/80 backdrop-blur-lg border-b border-slate-200">
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="ghost" />
            <NotificationDropdown accentColor="violet" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 pl-2 pr-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-violet-600 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium">{resellerName}</p>
                    <p className="text-xs text-slate-500">{t("reseller")}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b">
                  <p className="text-sm font-medium">{resellerName}</p>
                  <p className="text-xs text-slate-500">{resellerCode || t("reseller")}</p>
                </div>
                <DropdownMenuItem onClick={() => navigate(createPageUrl("ResellerSettings"))}>
                  <Settings className="h-4 w-4 mr-2" />
                  {t("settings")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-red-600" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6 pt-20 lg:pt-6 min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  );
}

export default function ResellerLayout(props) {
  return <ResellerLayoutContent {...props} />;
}